import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useFetch from "../../hooks/useFetch";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import MainLayout from "../../layouts/MainLayout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMicrophone, faMicrophoneSlash, faPaperPlane, faRedo, faRobot } from "@fortawesome/free-solid-svg-icons";
import SpeechInputComponent from '../../components/SpeechInputComponent';

const AIInterview = () => {
    const authState = useSelector((state) => state.authReducer);
    const navigate = useNavigate();
    const [fetchData, { loading }] = useFetch();

    // User details
    const [userId] = useState(authState.user._id);
    const [userName] = useState(authState.user.name);
    const [userCode] = useState(authState.user.think5whyscode);

    // State for interview inputs
    const [language, setLanguage] = useState("English");
    const [jobSkill, setJobSkill] = useState(""); 
    const [interviewType, setInterviewType] = useState("Mock");
    const [questions, setQuestions] = useState("");
    const [response, setResponse] = useState("");
    const [feedback, setFeedback] = useState("");
    const [formErrors, setFormErrors] = useState({});
    const [showMicrophoneAlert, setShowMicrophoneAlert] = useState(false);

    // Speech recognition
    const { transcript, listening, resetTranscript, startSpeaking, stopSpeaking } = useSpeechRecognition();

    const fieldError = (field) => (
        <p className={`mt-1 text-pink-600 text-sm ${formErrors[field] ? "block" : "hidden"}`}>
          <i className='mr-2 fa-solid fa-circle-exclamation'></i>
          {formErrors[field]}
        </p>
      )

    // Update response when transcript changes
    useEffect(() => {
        setResponse(transcript);
    }, [transcript]);

    // // Debugging jobSkill updates
    // useEffect(() => {
    //     console.log("Updated jobSkill:", jobSkill);
    // }, [jobSkill]);

    const fetchQuestion = useCallback(() => {
        // console.log("Fetching question with:", { userId, language, jobSkill, interviewType });

        const config = {
            url: "/aiinterview/questions",
            method: "post",
            headers: { Authorization: authState.token },
            data: { userId: userId, language: language, jobSkill: jobSkill, interviewType: interviewType },
        };

        fetchData(config, { showSuccessToast: false })
            .then((data) => {
                if (data && data.question) {
                    setQuestions(data.question);
                } else {
                    console.error("No data received!");
                    setQuestions("");
                }
            })
            .catch((error) => {
                console.error("Error fetching questions:", error);
                setQuestions("");
            });
    }, [authState.userId, 
        authState.token, 
        userId, 
        language, 
        jobSkill, 
        interviewType, 
        fetchData]);

    useEffect(() => {
        fetchQuestion();
    }, [fetchQuestion]);

    // Submit user response
    const submitResponse = async () => {
        try {
            // console.log("Submitting response:", { response });
            const res = await axios.post("/api/interview/submit", { question: questions, response });
            setFeedback(res.data.feedback);
        } catch (error) {
            console.error("Error submitting response:", error);
        }
    };

    return (
        <MainLayout>
            <div className="flex flex-col bg-gray-700 mt-12 px-4 py-4">
                <p className="text-xl text-yellow-200 flex justify-center">AI Interview</p>

                {/* Language Selection */}
                <label>Language:</label>
                <select style={{ width: "250px" }} value={language} onChange={(e) => setLanguage(e.target.value)}>
                    <option value="English">English</option>
                    <option value="Hindi">Hindi</option>
                    <option value="Marathi">Marathi</option>
                    <option value="Gujarati">Gujarati</option>
                </select>

                {/* Job Skill Selection */}
                <label>Job Skill:</label>
                <select style={{ width: "250px" }} value={jobSkill} onChange={(e) => setJobSkill(e.target.value)}>
                    <option value="">Select Job Skill</option>
                    <option value="Electrician">Electrician</option>
                    <option value="Plumber">Plumber</option>
                </select>

                {/* Interview Type Selection */}
                <label>Interview Type:</label>
                <select style={{ width: "150px" }} value={interviewType} onChange={(e) => setInterviewType(e.target.value)}>
                    <option value="Mock">Mock</option>
                    <option value="Real">Real</option>
                </select>

                {/* Start Interview */}
                <button className="mt-4 w-40" onClick={fetchQuestion}>
                    <FontAwesomeIcon className="mr-2" icon={faRobot} /> Start Interview
                </button>

                {/* Display Questions */}
                {questions && <h3>Question: {questions}</h3>}

                <div className="border border-yellow-200 py-4 px-4 mt-4">
                    {/* Voice Controls */}
                    <div className="flex flex-row justify-center space-x-4 mt-4">
                        <button className="w-40 flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg shadow-md hover:bg-blue-600" onClick={()=>!setShowMicrophoneAlert}>
                            <FontAwesomeIcon className="mr-2" icon={faMicrophone} /> Start Voice
                        </button>
                        <button className="w-40 flex items-center justify-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg shadow-md hover:bg-red-600" onClick={stopSpeaking}>
                            <FontAwesomeIcon className="mr-2" icon={faMicrophoneSlash} /> Stop Voice
                        </button>
                        <button className="w-40 flex items-center justify-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg shadow-md hover:bg-gray-600" onClick={resetTranscript}>
                            <FontAwesomeIcon className="mr-2" icon={faRedo} /> Reset
                        </button>
                    </div>

                    { showMicrophoneAlert ? (
                        <SpeechInputComponent
                          label="for Description"
                          name="response"
                          value={response}
                          onChange={(e) => setResponse(e.target.value)}
                          placeholder="Click Mic and Speak or Talk for Voice Input..."
                          maxLength="2000"
                        />
                    ) : (
                      <textarea 
                        name="response" 
                        id="response" 
                        value={response}
                        placeholder={showMicrophoneAlert ? "Write here or Click Mic for Voice Input...":"Write here.."}
                        onChange={(e) => setResponse(e.target.value)}
                        maxLength="2000"
                        style={{
                          border: '1px solid #ccc',
                          padding: '2px',
                          backgroundColor: '#f0f0f0', 
                          color: '#000000',
                          maxWidth: '100%', 
                          maxHeight: '150px' 
                        }}
                      />
                    )}
                  {fieldError("response")}

                </div>
                {/* Transcript Display */}
                <p className="mt-4 mb-4 px-4 py-4">{transcript}</p>

                {/* Submit Response */}
                <div className="flex flex-row justify-center">
                    <button className="w-40 flex flex-row justify-center" onClick={submitResponse}>
                        <FontAwesomeIcon className="mr-2" icon={faPaperPlane} /> Submit Answer
                    </button>
                </div>

                {/* Feedback Display */}
                {feedback && <p className="mt-2 mb-2 px-2 py-2">Feedback: {feedback}</p>}
            </div>
        </MainLayout>
    );
};

export default AIInterview;
